import { zodResolver } from '@hookform/resolvers/zod'
import {
  Camera,
  ChevronDown,
  Globe,
  Layers,
  Mic,
  Sparkles,
  Type,
  Wand2,
} from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useStartMirage, type MirageInput } from '@/api/endpoints/factories'
import { FormErrorSummary } from '@/components/feedback/FormErrorSummary'
import { JobProgressPanel } from '@/components/feedback/JobProgressPanel'
import { BackToFactoryLink } from '@/features/factory/components/BackToFactoryLink'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Segmented, type SegmentedOption } from '@/components/ui/Segmented'
import { useActiveChannelFromUrl } from '@/hooks/useActiveChannel'
import { LlmProviderPicker } from '../_shared/LlmProviderPicker'
import { PresetBar } from '../_shared/PresetBar'
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import { cn } from '@/lib/cn'
import styles from './MirageForm.module.scss'

const FIELD_LABELS: Record<string, string> = {
  topic: 'Tópico',
  scene_count: 'Cenas',
  language: 'Idioma',
  image_style: 'Estilo de imagem',
  image_provider: 'Provedor de imagem',
  video_aspect: 'Formato',
  video_provider: 'Provedor de vídeo',
  video_model: 'Modelo de vídeo',
  voice: 'Voz',
  extra_direction: 'Direção extra',
}

const formSchema = z.object({
  topic: z
    .string()
    .min(3, 'Descreva o tópico em pelo menos 3 caracteres')
    .max(500, 'Máximo de 500 caracteres'),
  scene_count: z.number().int().min(1).max(12),
  language: z.enum(['pt-br', 'en']),
  image_style: z.string().min(1),
  image_provider: z.enum(['hf', 'pollinations']),
  video_aspect: z.enum(['shorts', 'video']),
  video_provider: z.enum(['veo', 'ltx', 'fal']),
  video_model: z.string(),
  voice: z.string(),
  extra_direction: z.string().max(800, 'Máximo de 800 caracteres'),
  llm_provider: z.enum(['auto', 'cloud', 'local']).optional(),
})

type FormValues = z.infer<typeof formSchema>

const FORM_DEFAULTS: FormValues = {
  topic: '',
  scene_count: 4,
  language: 'pt-br',
  image_style: 'cinematic',
  image_provider: 'hf',
  video_aspect: 'shorts',
  video_provider: 'veo',
  video_model: 'veo-3.1-fast-generate-preview',
  voice: '',
  extra_direction: '',
}

function formToPayload(values: FormValues): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    topic: values.topic,
    scene_count: values.scene_count,
    language: values.language,
    image_style: values.image_style,
    image_provider: values.image_provider,
    video_aspect: values.video_aspect,
    video_provider: values.video_provider,
    video_model: values.video_model,
  }
  if (values.voice) payload.voice = values.voice
  if (values.extra_direction) payload.extra_direction = values.extra_direction
  return payload
}

function payloadToForm(params: Record<string, unknown>): FormValues {
  return {
    ...FORM_DEFAULTS,
    topic: (params.topic as string | undefined) ?? FORM_DEFAULTS.topic,
    scene_count: (params.scene_count as number | undefined) ?? FORM_DEFAULTS.scene_count,
    language:
      (params.language as FormValues['language'] | undefined) ?? FORM_DEFAULTS.language,
    image_style: (params.image_style as string | undefined) ?? FORM_DEFAULTS.image_style,
    image_provider:
      (params.image_provider as FormValues['image_provider'] | undefined) ??
      FORM_DEFAULTS.image_provider,
    video_aspect:
      (params.video_aspect as FormValues['video_aspect'] | undefined) ??
      FORM_DEFAULTS.video_aspect,
    video_provider:
      (params.video_provider as FormValues['video_provider'] | undefined) ??
      FORM_DEFAULTS.video_provider,
    video_model: (params.video_model as string | undefined) ?? FORM_DEFAULTS.video_model,
    voice: (params.voice as string | undefined) ?? '',
    extra_direction: (params.extra_direction as string | undefined) ?? '',
  }
}

const LANGUAGE_OPTIONS: ReadonlyArray<SegmentedOption<'pt-br' | 'en'>> = [
  { value: 'pt-br', label: 'Português' },
  { value: 'en', label: 'English' },
]

const ASPECT_OPTIONS: ReadonlyArray<SegmentedOption<'shorts' | 'video'>> = [
  { value: 'shorts', label: '9:16 · Shorts' },
  { value: 'video', label: '16:9 · Vídeo' },
]

// HF first because it's the default — Pollinations stays as the no-auth fallback.
const PROVIDER_OPTIONS: ReadonlyArray<SegmentedOption<'hf' | 'pollinations'>> = [
  { value: 'hf', label: 'HuggingFace' },
  { value: 'pollinations', label: 'Pollinations · sem auth' },
]

// Mirror the keys registered in src/services/image_generation/styles.py.
const IMAGE_STYLE_OPTIONS = [
  { value: 'cinematic', label: 'Cinematic' },
  { value: 'photo', label: 'Photo' },
  { value: 'editorial', label: 'Editorial' },
  { value: 'sketch', label: 'Sketch' },
  { value: 'watercolor', label: 'Watercolor' },
  { value: 'anime', label: 'Anime' },
  { value: '3d', label: '3D' },
  { value: 'pixel', label: 'Pixel' },
  { value: 'vector', label: 'Vector' },
  { value: 'none', label: 'Sem estilo' },
] as const

// Mirror src/services/video_generation/models.py — first entry per
// provider is the canonical default.
const VIDEO_PROVIDER_OPTIONS: ReadonlyArray<SegmentedOption<'veo' | 'ltx' | 'fal'>> = [
  { value: 'veo', label: 'Veo · Gemini' },
  { value: 'fal', label: 'fal.ai · direct' },
  { value: 'ltx', label: 'LTX · HF (proxy)' },
]

const VIDEO_MODEL_OPTIONS: Record<'veo' | 'ltx' | 'fal', ReadonlyArray<{ value: string; label: string }>> = {
  veo: [
    { value: 'veo-3.1-fast-generate-preview', label: 'Veo 3.1 Fast (~60s/clip · 8s)' },
    { value: 'veo-3.1-generate-preview', label: 'Veo 3.1 Quality (~120s/clip · 8s)' },
  ],
  ltx: [
    { value: 'Lightricks/LTX-Video', label: 'LTX-Video Distilled (~45s/clip · 5s)' },
  ],
  fal: [
    { value: 'fal-ai/ltx-video-13b-distilled/image-to-video', label: 'LTX-Video 13B Distilled (~45s/clip · 5s)' },
    { value: 'fal-ai/ltx-video-13b/image-to-video', label: 'LTX-Video 13B Full (~90s/clip · 5s)' },
  ],
}

// Per-clip durations stay in sync with the backend ModelMeta.duration_s so
// the "≈ N segundos de vídeo" hint matches what gets generated.
const CLIP_DURATION_S: Record<string, number> = {
  'veo-3.1-fast-generate-preview': 8,
  'veo-3.1-generate-preview': 8,
  'Lightricks/LTX-Video': 5,
  'fal-ai/ltx-video-13b-distilled/image-to-video': 5,
  'fal-ai/ltx-video-13b/image-to-video': 5,
}

const PROVIDER_HELP: Record<'veo' | 'ltx' | 'fal', string> = {
  veo:
    'Pago por segundo na Gemini API, sem free tier: ~$0.80–0.96 por clip de 8s no Fast, ' +
    '~$3.20 no Quality. Usa a chave do Google salva em Configurações.',
  fal:
    'fal.ai direct — $5 grátis no signup (~80 clips), ~$0.05 por clip depois. ' +
    'Requer FAL_KEY no .env.',
  ltx:
    'LTX via HuggingFace Inference Providers (auto-roteado pra fal-ai). ' +
    'Usa créditos mensais do HF_TOKEN; pode esgotar.',
}

interface RowProps {
  icon: ReactNode
  label: ReactNode
  htmlFor?: string
  children: ReactNode
}

function Row({ icon, label, htmlFor, children }: RowProps) {
  return (
    <div className={styles.row}>
      {htmlFor ? (
        <label className={styles.rowLabel} htmlFor={htmlFor}>
          <span className={styles.rowIcon} aria-hidden>
            {icon}
          </span>
          {label}
        </label>
      ) : (
        <div className={styles.rowLabel}>
          <span className={styles.rowIcon} aria-hidden>
            {icon}
          </span>
          {label}
        </div>
      )}
      <div className={styles.rowControl}>{children}</div>
    </div>
  )
}

interface AdvSectionProps {
  icon: ReactNode
  label: ReactNode
  children: ReactNode
}

function AdvSection({ icon, label, children }: AdvSectionProps) {
  return (
    <div className={styles.advSection}>
      <div className={styles.advSectionLabel}>
        <span className={styles.rowIcon} aria-hidden>
          {icon}
        </span>
        {label}
      </div>
      {children}
    </div>
  )
}

export function MirageForm() {
  const { active } = useActiveChannelFromUrl()
  const channel = active?.slug ?? ''
  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const startMutation = useStartMirage()

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      topic: '',
      scene_count: 4,
      language: 'pt-br',
      image_style: 'cinematic',
      image_provider: 'hf',
      video_aspect: 'shorts',
      video_provider: 'veo',
      video_model: 'veo-3.1-fast-generate-preview',
      voice: '',
      extra_direction: '',
      llm_provider: 'auto',
    },
  })

  const language = form.watch('language')
  const provider = form.watch('image_provider')
  const aspect = form.watch('video_aspect')
  const style = form.watch('image_style')
  const videoProvider = form.watch('video_provider')
  const videoModel = form.watch('video_model')
  const sceneCount = form.watch('scene_count')
  const llmProvider = form.watch('llm_provider')

  // Snap video_model to the first option of the active provider whenever
  // the user switches provider (Veo Fast → LTX-Distilled, etc.). Keeps
  // the model dropdown's selected value valid without manual reset.
  useEffect(() => {
    const allowed = VIDEO_MODEL_OPTIONS[videoProvider].map((o) => o.value)
    const fallback = allowed[0]
    if (fallback !== undefined && !allowed.includes(videoModel)) {
      form.setValue('video_model', fallback, { shouldValidate: true })
    }
  }, [videoProvider, videoModel, form])

  const clipDurationS = CLIP_DURATION_S[videoModel] ?? 8

  const onSubmit = form.handleSubmit(
    (values) => {
      const payload: MirageInput = {
        topic: values.topic,
        scene_count: values.scene_count,
        language: values.language,
        image_style: values.image_style,
        image_provider: values.image_provider,
        video_aspect: values.video_aspect,
        video_provider: values.video_provider,
        video_model: values.video_model,
        voice: values.voice || '',
        extra_direction: values.extra_direction || '',
        llm_provider: values.llm_provider ?? 'auto',
      }
      startMutation.mutate(payload, {
        onSuccess: (data) => {
          setActiveJobId(data.job_id)
        },
      })
    },
    // Surface zod validation failures: without this branch the form
    // silently no-ops on invalid input — easy to mistake for "Gerar
    // doesn't do anything".
    (errors) => {
       
      console.warn('Mirage form validation failed:', errors)
    },
  )

  // Aggregate all field-level zod errors so the banner has something to
  // show even when the mutation hasn't been called yet.
  const fieldErrorEntries = Object.entries(form.formState.errors)
    .map(([field, err]) => {
      const message = (err as { message?: string } | undefined)?.message
      return message ? { field, message } : null
    })
    .filter((e): e is { field: string; message: string } => e !== null)

  if (activeJobId) {
    return (
      <div className={styles.panelShell}>
        <JobProgressPanel jobId={activeJobId} />
        <div className={styles.panelActions}>
          <Button
            variant="secondary"
            onClick={() => {
              setActiveJobId(null)
              form.reset()
            }}
          >
            Gerar outro
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.shell}>
      <div className={styles.content}>
        <header className={styles.header}>
          <div className={styles.kicker}>
            <Sparkles aria-hidden /> Mirage · text → image → video
          </div>
          <h1 className={styles.title}>Crie um vídeo a partir de um tópico</h1>
          <p className={styles.subtitle}>
            Mirage gera o roteiro, transforma cada cena em imagem e anima cada quadro
            (Veo ou LTX), narrando tudo no final. Pipeline 100% autônomo.
          </p>
        </header>

        <BackToFactoryLink />

        <form className={styles.glass} onSubmit={onSubmit} noValidate>
          {startMutation.isError ? (
            <FormErrorSummary error={startMutation.error} fieldLabels={FIELD_LABELS} />
          ) : null}
          {fieldErrorEntries.length > 0 && !startMutation.isError ? (
            <div className={styles.glassError ?? ''} role="alert" aria-live="polite">
              <strong>Verifique os campos abaixo:</strong>
              <ul style={{ margin: '0.5em 0 0 1.2em', padding: 0 }}>
                {fieldErrorEntries.map((e) => (
                  <li key={e.field}>
                    <span style={{ fontWeight: 600 }}>{FIELD_LABELS[e.field] ?? e.field}:</span>{' '}
                    {e.message}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {channel ? (
            <PresetBar<FormValues>
              factory="mirage"
              channel={channel}
              formValues={form.watch()}
              formToPayload={formToPayload}
              payloadToForm={payloadToForm}
              onApplyPreset={(values) => {
                form.reset(values)
              }}
            />
          ) : null}

          <div className={styles.rows}>
            <Row icon={<Wand2 />} label="Tópico" htmlFor="mr-topic">
              <textarea
                id="mr-topic"
                className={cn(styles.textarea, form.formState.errors.topic && styles.textareaError)}
                rows={3}
                placeholder="Ex.: Um farol abandonado guarda um segredo que só aparece em noites de neblina"
                {...form.register('topic')}
              />
              {form.formState.errors.topic ? (
                <span className={styles.errorText} role="alert">
                  {form.formState.errors.topic.message}
                </span>
              ) : (
                <span className={styles.hint}>
                  ≈ {sceneCount * clipDurationS} segundos de vídeo ({sceneCount} cena
                  {sceneCount === 1 ? '' : 's'} de {clipDurationS}s).
                </span>
              )}
            </Row>

            <Row icon={<Camera />} label="Formato">
              <Segmented
                value={aspect}
                onValueChange={(v) =>
                  { form.setValue('video_aspect', v, { shouldValidate: true }); }
                }
                options={ASPECT_OPTIONS}
              />
            </Row>

            <Row icon={<Type />} label="Estilo" htmlFor="mr-style">
              <SelectRoot
                value={style}
                onValueChange={(v) => { form.setValue('image_style', v, { shouldValidate: true }); }}
              >
                <SelectTrigger id="mr-style" aria-label="Estilo de imagem">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {IMAGE_STYLE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </SelectRoot>
            </Row>
          </div>

          {advancedOpen ? (
            <div className={styles.advanced}>
              <AdvSection icon={<Layers />} label="Cenas">
                <Input
                  id="mr-scenes"
                  type="number"
                  min={1}
                  max={12}
                  {...form.register('scene_count', { valueAsNumber: true })}
                />
              </AdvSection>

              <AdvSection icon={<Globe />} label="Idioma">
                <Segmented
                  value={language}
                  onValueChange={(v) =>
                    { form.setValue('language', v, { shouldValidate: true }); }
                  }
                  options={LANGUAGE_OPTIONS}
                />
              </AdvSection>

              <AdvSection icon={<Type />} label="Provedor de imagem">
                <Segmented
                  value={provider}
                  onValueChange={(v) =>
                    { form.setValue('image_provider', v, {
                      shouldValidate: true,
                    }); }
                  }
                  options={PROVIDER_OPTIONS}
                />
                <span className={styles.hint}>
                  HuggingFace usa o seu <code>HF_TOKEN</code>. Pollinations não exige auth (mas
                  costuma ter qualidade menor).
                </span>
              </AdvSection>

              <AdvSection icon={<Sparkles />} label="Provedor de vídeo">
                <Segmented
                  value={videoProvider}
                  onValueChange={(v) =>
                    { form.setValue('video_provider', v, { shouldValidate: true }); }
                  }
                  options={VIDEO_PROVIDER_OPTIONS}
                />
                <span className={styles.hint}>{PROVIDER_HELP[videoProvider]}</span>
              </AdvSection>

              <AdvSection icon={<Sparkles />} label="Modelo de vídeo">
                <SelectRoot
                  value={videoModel}
                  onValueChange={(v) => { form.setValue('video_model', v, { shouldValidate: true }); }}
                >
                  <SelectTrigger id="mr-video-model" aria-label="Modelo de vídeo">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VIDEO_MODEL_OPTIONS[videoProvider].map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </SelectRoot>
              </AdvSection>

              <AdvSection icon={<Mic />} label="Voz">
                <Input
                  id="mr-voice"
                  placeholder="Edge TTS voice id — vazio = padrão do idioma"
                  {...form.register('voice')}
                />
              </AdvSection>

              <AdvSection icon={<Wand2 />} label="Direção extra">
                <textarea
                  id="mr-extra"
                  className={styles.textarea}
                  rows={2}
                  placeholder="Opcional: tom, personagem, restrições para o roteirista…"
                  {...form.register('extra_direction')}
                />
              </AdvSection>

              <LlmProviderPicker
                value={llmProvider ?? 'auto'}
                onChange={(v) => {
                  form.setValue('llm_provider', v)
                }}
              />
            </div>
          ) : null}

          <footer className={styles.cta}>
            <button
              type="button"
              className={cn(styles.advToggle, advancedOpen && styles.advToggleOpen)}
              onClick={() => {
                setAdvancedOpen((o) => !o)
              }}
            >
              Ajuste fino <ChevronDown aria-hidden />
            </button>
            <div className={styles.ctaSpacer} />
            <button
              type="submit"
              className={styles.generateBtn}
              disabled={startMutation.isPending}
            >
              <Sparkles aria-hidden />
              {startMutation.isPending ? 'Iniciando…' : 'Gerar'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  )
}
